import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  AminIdLoginCredentials,
  authService,
} from '../../services/authService';

interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  token: string | null;
  requires2fa: boolean;
  challengeToken: string | null;
  allowedFactors: string[];
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  requires2fa: false,
  challengeToken: null,
  allowedFactors: [],
  loading: false,
  error: null,
};

export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (token: string, { rejectWithValue }) => {
    try {
      const user = await authService.validateToken(token);
      localStorage.setItem('aminid_token', token);
      return { user, token };
    } catch (error: any) {
      localStorage.removeItem('aminid_token');
      return rejectWithValue(error.message);
    }
  }
);

export const loginWithCredentials = createAsyncThunk(
  'auth/loginWithCredentials',
  async (
    credentials: AminIdLoginCredentials,
    { rejectWithValue },
  ) => {
    try {
      const response = await authService.loginWithCredentials(credentials);

      if (response.requires2fa) {
        localStorage.removeItem('aminid_token');
        return {
          requires2fa: true,
          challengeToken: response.challengeToken || null,
          allowedFactors: response.allowedFactors || ['TOTP'],
          user: null,
          token: null,
        };
      }

      const token = response.tokens?.accessToken;
      if (!token) {
        throw new Error('AminID login failed');
      }

      const user = await authService.validateToken(token);
      localStorage.setItem('aminid_token', token);
      return {
        requires2fa: false,
        challengeToken: null,
        allowedFactors: [],
        user,
        token,
      };
    } catch (error: any) {
      localStorage.removeItem('aminid_token');
      return rejectWithValue(error.message);
    }
  },
);

export const verifySecondFactor = createAsyncThunk(
  'auth/verifySecondFactor',
  async (
    payload: { challengeToken: string; factorType: string; code: string },
    { rejectWithValue },
  ) => {
    try {
      const { accessToken } = await authService.verifySecondFactor(payload);
      const user = await authService.validateToken(accessToken);
      localStorage.setItem('aminid_token', accessToken);
      return { user, token: accessToken };
    } catch (error: any) {
      localStorage.removeItem('aminid_token');
      return rejectWithValue(error.message);
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('aminid_token');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithCredentials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifySecondFactor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateToken.fulfilled, (state, action: PayloadAction<{ user: any; token: string }>) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.requires2fa = false;
        state.challengeToken = null;
        state.allowedFactors = [];
        state.loading = false;
        state.error = null;
      })
      .addCase(loginWithCredentials.fulfilled, (state, action: PayloadAction<{
        user: any | null;
        token: string | null;
        requires2fa: boolean;
        challengeToken: string | null;
        allowedFactors: string[];
      }>) => {
        state.isAuthenticated = !action.payload.requires2fa;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.requires2fa = action.payload.requires2fa;
        state.challengeToken = action.payload.challengeToken;
        state.allowedFactors = action.payload.allowedFactors;
        state.loading = false;
        state.error = null;
      })
      .addCase(verifySecondFactor.fulfilled, (state, action: PayloadAction<{ user: any; token: string }>) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.requires2fa = false;
        state.challengeToken = null;
        state.allowedFactors = [];
        state.loading = false;
        state.error = null;
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.requires2fa = false;
        state.challengeToken = null;
        state.allowedFactors = [];
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginWithCredentials.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.requires2fa = false;
        state.challengeToken = null;
        state.allowedFactors = [];
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifySecondFactor.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.requires2fa = true;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.requires2fa = false;
        state.challengeToken = null;
        state.allowedFactors = [];
      });
  },
});

export default authSlice.reducer;
